<template>
  <div class="referral-pipeline">
    <div class="rp-toolbar">
      <input class="rp-search"
             type="search"
             :value="search"
             :placeholder="type === 'Reverse' ? 'Search borrower, email, loan officer, city...' : 'Search entity, contact, loan officer, city...'"
             @input="search = $event.target.value" />

      <!-- Wider pill only when the server says this user has more than their own (admins: All, BMs: My branch). -->
      <div v-if="allLabel" class="rp-segment" role="group" aria-label="Whose referrals">
        <button type="button" class="rp-segment__btn" :class="{ active: view === 'mine' }" @click="setView('mine')">Personal</button>
        <button type="button" class="rp-segment__btn" :class="{ active: view === 'all' }" @click="setView('all')">{{ allLabel }}</button>
      </div>

      <button v-if="unreadCount" type="button" class="rp-btn" @click="markAllRead">
        <i class="fa-solid fa-check-double"></i> Mark all read
      </button>
      <button type="button" class="rp-btn" :disabled="loading" @click="load">
        <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': loading }"></i> Refresh
      </button>
    </div>

    <!-- Stage chips filter the list; clicking the picked one again goes back to every stage. -->
    <div class="rp-stage-chips">
      <button type="button" class="rp-chip" :class="{ 'is-on': !stageFilter }" @click="stageFilter = ''">
        All <span class="rp-chip__count">{{ rows.length }}</span>
      </button>
      <button v-for="stage in stages"
              :key="stage.code"
              type="button"
              class="rp-chip"
              :class="{ 'is-on': stageFilter === stage.code }"
              @click="stageFilter = stageFilter === stage.code ? '' : stage.code">
        <span class="rp-chip__dot" :style="{ background: stage.color || '#6b7280' }"></span>
        {{ stage.label }}
        <span class="rp-chip__count">{{ stageCounts[stage.code] || 0 }}</span>
      </button>
    </div>

    <div v-if="error" class="rp-error">{{ error }}</div>

    <CommandCenterTable :columns="columns"
                        :data="visibleRows"
                        :show-header="false"
                        :numbered-pager="true"
                        :active-sort="sort"
                        :empty-text="loading ? 'Loading...' : emptyText"
                        @sort="onSort"
                        @row-click="openRow">
      <template #unreadCell="{ dataItem }">
        <span v-if="dataItem.isunread" class="rp-unread-dot" title="New activity since you last opened it"></span>
      </template>
      <template #nameCell="{ dataItem }">
        <span :class="{ 'rp-name--unread': dataItem.isunread }">{{ dataItem.displayname || '—' }}</span>
      </template>
      <template #statusCell="{ dataItem }">
        <span class="rp-status-pill" :style="pillStyle(dataItem.statuscode)">{{ stageLabel(dataItem.statuscode) }}</span>
      </template>
      <template #notesCell="{ dataItem }">
        <span v-if="dataItem.notecount" class="rp-notes" :title="`${dataItem.notecount} note(s)`">
          <i class="fa-regular fa-comment"></i> {{ dataItem.notecount }}
        </span>
      </template>
    </CommandCenterTable>

    <ReferralDrawer v-if="selected"
                    :type="type"
                    :referral="selected"
                    :stages="stages"
                    :can-set-status="canSetStatus"
                    @close="selected = null"
                    @changed="onDrawerChanged" />
  </div>
</template>

<script>
  import { orderBy } from '@progress/kendo-data-query';
  import CommandCenterTable from '../commandCenter/CommandCenterTable.vue';
  import ReferralDrawer from './ReferralDrawer.vue';
  import apiService from '../../api/api.service.js';
  import auth from '../../authConfig.js';
  import formatHelpers from '../../helpers/formatHelpers.js';
  import { lowerKeys, utcDate } from './referralHelpers.js';

  /**
   * One referral tab on the Pipeline page (type 'Reverse' | 'Commercial'): list, stage chips, Mine / All pill and
   * the notes + status drawer. Which rows come back is decided by the API (ReferralPipelineController) from the
   * signed-in user's roles; this component only asks for 'mine' or 'all'.
   */
  export default {
    name: 'ReferralPipeline',
    components: { CommandCenterTable, ReferralDrawer },
    props: {
      type: { type: String, required: true },
      // GetMe response (lowercased keys): reverseall label, commercialall label, isreverseadmin, ...
      me: { type: Object, default: null },
      // This type's stages, in order.
      stages: { type: Array, default: () => [] },
    },
    emits: ['unread'],
    data() {
      return {
        rows: [],
        loading: false,
        error: '',
        search: '',
        stageFilter: '',
        view: 'all',
        sort: [{ field: 'lastactivityat', dir: 'desc' }],
        selected: null,
      };
    },
    computed: {
      allLabel() {
        if (!this.me) return null;
        return this.type === 'Reverse' ? this.me.reversealllabel : this.me.commercialalllabel;
      },
      canSetStatus() {
        if (!this.me) return false;
        return !!(this.me.iscorp || (this.type === 'Reverse' ? this.me.isreverseadmin : this.me.iscommercialadmin));
      },
      stageMap() {
        return Object.fromEntries(this.stages.map(s => [s.code, s]));
      },
      stageCounts() {
        const counts = {};
        this.rows.forEach(r => { counts[r.statuscode] = (counts[r.statuscode] || 0) + 1; });
        return counts;
      },
      unreadCount() {
        return this.rows.filter(r => r.isunread).length;
      },
      emptyText() {
        return this.view === 'mine' || !this.allLabel
          ? `You have no ${this.type.toLowerCase()} referrals yet.`
          : `No ${this.type.toLowerCase()} referrals yet.`;
      },
      columns() {
        const lead = [
          { cell: 'unreadCell', title: '', field: '__unread', sortable: false, filterable: false, width: 28 },
          { cell: 'nameCell', field: 'displayname', title: this.type === 'Reverse' ? 'Borrower' : 'Entity / Contact' },
          { cell: 'statusCell', field: 'stagesort', title: 'Status' },
        ];
        const middle = this.type === 'Reverse'
          ? [
              { field: 'program',    title: 'Program'    },
              { field: 'location',   title: 'Location'   },
              { field: 'loanamount', title: 'Est. Value' },
            ]
          : [
              { field: 'propertytype', title: 'Property Type' },
              { field: 'loanpurpose',  title: 'Purpose'       },
              { field: 'loanamount',   title: 'Loan Amount'   },
              { field: 'location',     title: 'Location'      },
            ];
        return [
          ...lead,
          ...middle,
          { field: 'loname',         title: 'Loan Officer'                    },
          { field: 'submittedat',    title: 'Submitted',     type: 'datetime' },
          { field: 'lastactivityat', title: 'Last Activity', type: 'datetime' },
          { cell: 'notesCell', title: 'Notes', field: 'notecount', filterable: false },
        ];
      },
      visibleRows() {
        let items = this.rows;
        if (this.stageFilter) items = items.filter(r => r.statuscode === this.stageFilter);
        if (this.search) {
          const q = this.search.toLowerCase();
          items = items.filter(r => r.searchtext.includes(q));
        }
        return this.sort?.length ? orderBy(items, this.sort) : items;
      },
    },
    watch: {
      // Admins and BMs start on the wider list; everyone else only has their own.
      allLabel: { immediate: true, handler(label) { this.view = label ? 'all' : 'mine'; } },
      stages() { this.rows = this.rows.map(r => this.decorate(r)); },
    },
    created() {
      this.load();
    },
    methods: {
      async load() {
        this.loading = true;
        this.error = '';
        try {
          const token = await auth.acquireTokenResponse();
          const res = await apiService.GetReferrals(this.type, this.view, token.accessToken);
          this.rows = (res?.data?.data ?? []).map(r => this.decorate(lowerKeys(r)));
          this.$emit('unread', this.unreadCount);
          // Keep an open drawer pointing at the fresh row (status / note count changed).
          if (this.selected) this.selected = this.rows.find(r => r.id === this.selected.id) || this.selected;
        } catch (err) {
          this.error = err?.response?.data?.message || `Could not load ${this.type.toLowerCase()} referrals.`;
        } finally {
          this.loading = false;
        }
      },
      decorate(r) {
        const row = { ...r };
        row.submittedat = utcDate(r.submittedat);
        row.lastactivityat = utcDate(r.lastactivityat);
        if (row.phone) row.phone = formatHelpers.formatPhoneNumber(row.phone);
        row.location = [r.city, r.state].filter(Boolean).join(', ');
        row.stagesort = this.stageMap[r.statuscode]?.sortorder ?? 0;
        row.searchtext = [row.displayname, row.email, row.phone, row.loname, row.location, row.program,
                          row.propertytype, row.loanpurpose, this.stageLabel(r.statuscode)]
          .filter(Boolean).join(' ').toLowerCase();
        return row;
      },
      setView(view) {
        if (this.view === view) return;
        this.view = view;
        this.load();
      },
      onSort(sort) {
        this.sort = Array.isArray(sort) ? sort : (sort?.field ? [sort] : []);
      },
      openRow(row) {
        this.selected = row;
      },
      onDrawerChanged() {
        this.load();
      },
      async markAllRead() {
        try {
          const token = await auth.acquireTokenResponse();
          await apiService.MarkAllReferralsSeen(this.type, token.accessToken);
          this.rows = this.rows.map(r => ({ ...r, isunread: false }));
          this.$emit('unread', 0);
        } catch (err) {
          this.error = err?.response?.data?.message || 'Could not mark these as read.';
        }
      },
      stageLabel(code) {
        return this.stageMap[code]?.label || code || 'New Referral';
      },
      pillStyle(code) {
        const color = this.stageMap[code]?.color || '#6b7280';
        return { color, background: color + '1a', borderColor: color + '40' };
      },
    },
  };
</script>

<style scoped>
  /* Fill the tab down to the bottom of the page: the table card takes whatever height the toolbar and chips
     leave, and scrolls its rows inside (CommandCenterTable's .cc-table-wrap is flex: 1 1 0, so it needs a
     parent with a real height or it collapses to nothing). */
  .referral-pipeline { flex: 1 1 auto; display: flex; flex-direction: column; gap: 12px; padding: 4px 0 16px; min-height: 0; height: 100%; }
  .referral-pipeline > :deep(.cc-table-card) { flex: 1 1 auto; min-height: 320px; height: auto; }

  .rp-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
  .rp-search {
    flex: 1 1 280px; height: 42px; border: 1px solid #e6e8ec; border-radius: 12px; background: #fff;
    padding: 0 14px; font-size: 14px; color: #111827;
  }
  .rp-search:focus { outline: 0; border-color: #398b87; box-shadow: 0 0 0 3px rgba(57, 139, 135, .15); }

  .rp-segment { display: inline-flex; gap: 2px; height: 42px; align-items: center; background: #fff; border: 1px solid #e6e8ec; border-radius: 999px; padding: 3px; }
  .rp-segment__btn { border: 0; border-radius: 999px; background: transparent; padding: 6px 14px; font-size: 13px; font-weight: 500; color: #4b5260; cursor: pointer; }
  .rp-segment__btn.active { background: var(--primary, #051d3a); color: #fff; }
  .rp-segment__btn:not(.active):hover { background: #f6f7f9; }

  .rp-btn {
    height: 34px; padding: 0 12px; border-radius: 999px; border: 1px solid #d9dce1; background: #fff;
    color: #111827; font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  }
  .rp-btn:hover:not(:disabled) { background: #f6f7f9; }
  .rp-btn:disabled { opacity: .6; cursor: default; }

  .rp-stage-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .rp-chip {
    display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 10px; border-radius: 999px;
    border: 1px solid #e6e8ec; background: #fff; color: #4b5260; font-size: 12px; font-weight: 500; cursor: pointer;
  }
  .rp-chip:hover { background: #f6f7f9; }
  .rp-chip.is-on { border-color: var(--primary, #051d3a); background: var(--primary, #051d3a); color: #fff; }
  .rp-chip__dot { width: 8px; height: 8px; border-radius: 50%; }
  .rp-chip__count { font-weight: 700; opacity: .8; }

  .rp-error { padding: 10px 12px; border-radius: 8px; background: #fef2f2; color: #b91c1c; font-size: 13px; }

  .rp-unread-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: #398b87; box-shadow: 0 0 0 3px rgba(57, 139, 135, .18); }
  .rp-name--unread { font-weight: 700; color: #111827; }
  .rp-status-pill { display: inline-block; padding: 2px 10px; border-radius: 999px; border: 1px solid; font-size: 12px; font-weight: 600; white-space: nowrap; }
  .rp-notes { color: #6b7280; font-size: 12px; white-space: nowrap; }
</style>
